/**
 * El documento de cifras en el cable del Despacho (ADR-22 pt 3 y 8; M6b,
 * paso 4a): «Tus cifras», la superficie de las fuentes donde un Client
 * Admin sube una hoja (Excel o CSV), revisa el borrador con sus totales,
 * corrige el mapeo eligiendo entre opciones cerradas y lo fija. La lista
 * de documentos la ve todo Executive de la casa; el resto, solo el Client
 * Admin (403 `admin` si no).
 *
 * - `upload`: el cuerpo es el FICHERO CRUDO (como el adjunto), con el
 *   nombre en `?name=` y, si la casa lo ha dicho tras `needs-separator`,
 *   `?separator=semicolon|comma|tab`. Que la hoja no se deje leer no es un
 *   error del canal: `status: "rejected"` (HTTP 200) con la frase. El
 *   fichero no se guarda: solo nombre, tamaño, hash y fechas (pt 1).
 * - `mapping`: la corrección, con opciones cerradas — hojas, columnas,
 *   conceptos y dimensiones que la propia vista ofrece —; nada de texto
 *   libre que lea un modelo (pt 3 d). El comando vuelve a sumar.
 * - `fix`: el visto bueno. `invalid` (HTTP 200) dice qué falta o qué
 *   bloquea, sin cifras.
 *
 * Errores HTTP: `session` 401 · `admin` 403 · `no-draft` 404 · `not-found`
 * 404 · `changed` 409 (el borrador ya no es el que se revisó) · `not-fixed`
 * 409 · `too-large` 413 · `invalid` 400.
 *
 * No hace: pintar la pantalla (paso 4c) ni decidir nada del documento; los
 * tipos son la forma del cable y nada más.
 */
import { z } from "zod";
import { DespachoFiguresDocuments, FiguresDocumentWire } from "./despacho.js";

export const FIGURES_DOCUMENT_ROUTES = {
  /** GET: la lista de documentos de mi casa (todo Executive). */
  documents: "/despacho/figures/documents",
  /** POST: retirar un documento fijado (Client Admin). */
  retire: (id: string) => `/despacho/figures/documents/${encodeURIComponent(id)}/retire`,
  retirePattern: "/despacho/figures/documents/:id/retire",
  /** GET: el borrador vivo (Client Admin). */
  draft: "/despacho/figures/draft",
  /** POST: subir una hoja (cuerpo crudo, `?name=`); sustituye al borrador vivo. */
  upload: "/despacho/figures/draft/upload",
  /** PUT: corregir el mapeo. */
  mapping: "/despacho/figures/draft/mapping",
  /** POST: fijar (el visto bueno). */
  fix: "/despacho/figures/draft/fix",
  /** POST: descartar el borrador. */
  discard: "/despacho/figures/draft/discard",
} as const;

export const FIGURES_DOCUMENT_LIMITS = {
  /** 10 MB de hoja (el tope de lectura del paso 3, `DEFAULT_SHEET_LIMITS.maxBytes`). */
  fileBytes: 10 * 1024 * 1024,
  nameChars: 200,
  /** Filas de muestra por hoja que la vista enseña: la pantalla no es una hoja de cálculo. */
  sampleRows: 3,
  /** Largo de un valor en una regla de exclusión. */
  ruleValueChars: 200,
} as const;

/** Para el `accept` del selector de ficheros (ADR-22 pt 5). */
export const FIGURES_DOCUMENT_ACCEPT = ".xlsx,.xls,.ods,.csv";

/** El separador de un CSV, cuando la casa lo dice (`?separator=`). */
export const FiguresSeparator = z.enum(["semicolon", "comma", "tab"]);
export type FiguresSeparator = z.infer<typeof FiguresSeparator>;

/** Formatos de periodo que el comando sabe leer (los del paso 3). */
export const FIGURES_PERIOD_FORMATS = ["aaaa-mm-dd", "dd/mm/aaaa", "mm/dd/aaaa", "dd-mm-aaaa", "dd.mm.aaaa", "aaaa-mm", "aaaa/mm", "mm/aaaa", "mes aaaa", "mes-aa"] as const;
export const FiguresPeriodFormat = z.enum(FIGURES_PERIOD_FORMATS);

/** Formato de los números: `es` 1.234,56 · `en` 1,234.56 · `unsure` sin decidir (no se suma). */
export const FiguresNumberFormat = z.enum(["es", "en", "unsure"]);

/** El nombre visible de cada dimensión del catálogo (nunca el identificador a la vista). */
export const FIGURES_DIMENSION_LABELS: Readonly<Record<string, string>> = {
  customer: "Cliente",
  vendor: "Proveedor",
  product_line: "Línea de producto",
  salesperson: "Comercial",
  employee: "Empleado",
  bucket: "Tramo",
  account: "Cuenta",
  item: "Artículo",
  cost_center: "Centro de coste",
  project: "Proyecto",
  contract: "Contrato",
  work_type: "Tipo de trabajo",
  job_category: "Categoría",
  contract_type: "Tipo de contrato",
  work_mode: "Modalidad",
  channel: "Canal",
  reason: "Motivo",
  bank: "Entidad",
  instrument: "Instrumento",
  due_period: "Vencimiento",
};

const isoDay = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);
const column = z.string().min(1).max(200);
const ruleValue = z.string().min(1).max(FIGURES_DOCUMENT_LIMITS.ruleValueChars);

/** Una regla cerrada para descartar filas: igual a, empieza por, o vacía (el valor, uno de los que tiene la columna). */
export const FiguresExcludeRule = z.union([
  z.object({ column, equals: ruleValue }).strict(),
  z.object({ column, startsWith: ruleValue }).strict(),
  z.object({ column, empty: z.literal(true) }).strict(),
]);
export type FiguresExcludeRule = z.infer<typeof FiguresExcludeRule>;

/**
 * Lo que la casa elige de una hoja. La versión del concepto no se elige
 * (es la vigente), y las columnas de personas no se desmarcan: una hoja
 * con personas se arregla en el fichero (pt 5).
 */
export const SheetMappingEdit = z
  .object({
    sheet: z.string().min(1).max(200),
    /** El concepto del catálogo, o null: la hoja no entra. */
    concept: z.string().regex(/^[a-z][a-z0-9_]*$/).nullable(),
    /** La tabla que se produce: una fila por apunte (`larga`) o una columna por categoría (`cruzada`). */
    form: z.enum(["larga", "cruzada"]),
    /** Un flujo: la columna del mes o la fecha de cada fila. */
    periodColumn: column.nullable(),
    periodFormat: FiguresPeriodFormat.nullable(),
    /** Un saldo: la fecha del saldo. */
    periodFixed: isoDay.nullable(),
    valueColumn: column.nullable(),
    /** `{ dimensión: columna }`, de las que el concepto admite. */
    dimensionColumns: z.record(z.string().regex(/^[a-z][a-z_]*$/), column),
    numberFormat: FiguresNumberFormat,
    excludeRows: z.array(FiguresExcludeRule).max(20),
  })
  .strict();
export type SheetMappingEdit = z.infer<typeof SheetMappingEdit>;

/** El mapeo vigente de una hoja, como lo ve la casa. */
export const SheetMappingWire = SheetMappingEdit.extend({
  version: z.number().int().nullable(),
  /** Columnas que nombran a personas: la hoja no se carga hasta quitarlas del fichero. */
  personColumns: z.array(z.string()),
});
export type SheetMappingWire = z.infer<typeof SheetMappingWire>;

/** `PUT mapping`: lo que la casa corrige. Las hojas no nombradas siguen como estaban. */
export const FiguresMappingUpdate = z
  .object({
    draftId: z.string().min(1),
    /** La revisión que la casa vio (de `FiguresDraft.revision`): si cambió, 409 `changed`. */
    revision: z.string().min(1),
    sheets: z.array(SheetMappingEdit).max(50),
    /** La fecha de corte que la casa dice; null = la de las propias cifras (el final de la cobertura más tardía). Sin el campo, la que había. */
    cutOffDate: isoDay.nullable().optional(),
    /** ISO 4217. Sin el campo, la que había (EUR por defecto). */
    currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  })
  .strict();
export type FiguresMappingUpdate = z.infer<typeof FiguresMappingUpdate>;

/** `POST fix`: el visto bueno sobre la revisión que la casa vio. */
export const FiguresFixRequest = z.object({ draftId: z.string().min(1), revision: z.string().min(1) }).strict();
export type FiguresFixRequest = z.infer<typeof FiguresFixRequest>;

/** `POST discard`. */
export const FiguresDiscardRequest = z.object({ draftId: z.string().min(1) }).strict();
export type FiguresDiscardRequest = z.infer<typeof FiguresDiscardRequest>;

/** Un problema del borrador: hoja, código y la frase para la casa; nunca el contenido de una celda. */
export const FiguresProblem = z.object({
  sheet: z.string(),
  code: z.string(),
  message: z.string(),
  column: z.string().optional(),
  /** Números de fila del fichero (los primeros cincuenta). */
  rows: z.array(z.number().int()).optional(),
});
export type FiguresProblem = z.infer<typeof FiguresProblem>;

/** El resumen de una hoja para contrastar con el documento (pt 3 c): las cifras son de la casa y solo las ve ella. */
export const FiguresSheetSummary = z.object({
  /** `aggregated` produjo su tabla · `no-concept` no entra · `blocked` tiene problemas. */
  status: z.enum(["aggregated", "no-concept", "blocked"]),
  rowsRead: z.number().int(),
  rowsUsed: z.number().int(),
  rowsDiscarded: z.array(z.object({ reason: z.string(), count: z.number().int() })),
  rowsUnreadable: z.number().int(),
  /** Total en formato español («1.234,56»). */
  total: z.string().nullable(),
  unit: z.string().nullable(),
  /** «sep 2025». */
  firstMonth: z.string().nullable(),
  lastMonth: z.string().nullable(),
  monthsWithRows: z.number().int(),
  monthsWithoutRows: z.array(z.string()),
  totalsByMonth: z.array(z.object({ month: z.string(), total: z.string() })),
  /** `YYYY-MM-DD..YYYY-MM-DD` o `YYYY-MM-DD`. */
  coverage: z.string().nullable(),
});
export type FiguresSheetSummary = z.infer<typeof FiguresSheetSummary>;

/** Una hoja del borrador: su estructura (cabecera y tres filas como mucho), el mapeo vigente y su resumen. */
export const FiguresDraftSheet = z.object({
  name: z.string(),
  columns: z.array(z.string()),
  sample: z.array(z.array(z.string())).max(FIGURES_DOCUMENT_LIMITS.sampleRows),
  /** Filas de datos de la hoja. */
  rows: z.number().int(),
  /** null: nadie la ha mapeado (el modelo no propuso nada); la casa elige. */
  mapping: SheetMappingWire.nullable(),
  /** Lo que el modelo dijo de la hoja al proponer, si propuso. */
  modelNote: z.string().nullable(),
  confidence: z.enum(["high", "low"]).nullable(),
  summary: FiguresSheetSummary,
});
export type FiguresDraftSheet = z.infer<typeof FiguresDraftSheet>;

/** Un concepto que el mapeo ofrece (en producción, solo publicados), con lo que hace falta para los desplegables. */
export const FiguresConceptOption = z.object({
  id: z.string(),
  version: z.number().int(),
  label: z.string(),
  definition: z.string(),
  unit: z.string(),
  /** `flow`: un periodo, va mes a mes · `balance`: un saldo a una fecha. */
  temporal: z.enum(["flow", "balance"]),
  /** Las dimensiones admitidas, sin las de persona (pt 5: el mapeo no las ofrece). */
  dimensions: z.array(z.object({ id: z.string(), label: z.string() })),
});
export type FiguresConceptOption = z.infer<typeof FiguresConceptOption>;

/** Por qué el modelo no propuso mapeo (la casa mapea a mano); `no-concepts`: no hay ningún concepto que se pueda cargar aquí. */
export const FiguresProposalFailure = z.enum(["timeout", "model-error", "unreadable-output", "figures-in-output", "no-concepts"]);

/**
 * `GET draft` (y lo que devuelven subir y corregir): el borrador vivo de mi
 * casa tal como la pantalla lo pinta. Las cifras (totales, muestra) son de
 * la casa y solo las ve su Client Admin; los registros no llevan ninguna.
 */
export const FiguresDraft = z.object({
  /** La cabecera, como en la lista (estado `draft`). */
  document: FiguresDocumentWire,
  /** La revisión de lo que se ve (mapeo y tablas): se devuelve al corregir y al fijar. */
  revision: z.string(),
  /** La fecha de corte que llevarían las tablas; null si aún no se produce ninguna. */
  cutOffDate: z.string().nullable(),
  /** La que la casa ha dicho; null = la de sus cifras. */
  cutOffDateChosen: z.string().nullable(),
  currency: z.string(),
  proposal: z.discriminatedUnion("status", [z.object({ status: z.literal("proposed") }), z.object({ status: z.literal("none"), reason: FiguresProposalFailure })]),
  sheets: z.array(FiguresDraftSheet),
  /** Hojas del libro sin ninguna celda. */
  emptySheets: z.array(z.string()),
  problems: z.array(FiguresProblem),
  /** Hojas que no entran por no encajar en el catálogo (al equipo de Skein solo le llega este número). */
  tablesWithoutConcept: z.number().int(),
  filesProduced: z.number().int(),
  /** Se puede fijar: produce alguna tabla y no queda ningún problema. */
  ready: z.boolean(),
  concepts: z.array(FiguresConceptOption),
  periodFormats: z.array(FiguresPeriodFormat),
});
export type FiguresDraft = z.infer<typeof FiguresDraft>;

/** `GET draft`: el borrador vivo, o ninguno. */
export const FiguresDraftOutcome = z.object({ draft: FiguresDraft.nullable() });
export type FiguresDraftOutcome = z.infer<typeof FiguresDraftOutcome>;

/** Por qué una hoja no se lee (paso 3): la frase viaja en `message`. */
export const FiguresRejection = z.enum(["format", "too-big", "too-many-sheets", "too-many-rows", "too-many-cells", "unreadable", "empty", "needs-separator"]);
export type FiguresRejection = z.infer<typeof FiguresRejection>;

/** `POST upload`: el borrador nuevo (y si sustituyó a otro), o por qué la hoja no se lee. */
export const FiguresUploadOutcome = z.discriminatedUnion("status", [
  z.object({ status: z.literal("draft"), draft: FiguresDraft, replaced: z.boolean() }),
  z.object({ status: z.literal("rejected"), reason: FiguresRejection, message: z.string(), sheet: z.string().optional() }),
]);
export type FiguresUploadOutcome = z.infer<typeof FiguresUploadOutcome>;

/** Un bloqueo al fijar (pt 2): otra versión, otra definición u otra moneda que los fijados que ya sirven el concepto. */
export const FiguresConflict = z.object({
  concept: z.string(),
  label: z.string(),
  reason: z.enum(["version", "definition", "currency"]),
  /** Los documentos que hay que retirar antes (su nombre de fichero). */
  documents: z.array(z.object({ id: z.string(), fileName: z.string() })),
});
export type FiguresConflict = z.infer<typeof FiguresConflict>;

/** `GET documents` (y lo que devuelven fijar, descartar y retirar): si el asesor los sirve ahora y la lista, del más reciente al más antiguo. */
export const FiguresDocumentList = DespachoFiguresDocuments;
export type FiguresDocumentList = z.infer<typeof FiguresDocumentList>;

/** `POST fix`: fijado (con la lista nueva) o qué lo impide, sin cifras. */
export const FiguresFixOutcome = z.discriminatedUnion("status", [
  z.object({ status: z.literal("fixed"), documentId: z.string(), documents: FiguresDocumentList }),
  z.object({ status: z.literal("invalid"), problems: z.array(z.string()), conflicts: z.array(FiguresConflict) }),
]);
export type FiguresFixOutcome = z.infer<typeof FiguresFixOutcome>;
